import {ReactNode, useEffect, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {SpecialSelectionFilters} from './SpecialSelectionFilters'
import SharedData from '../../../common/SharedData'
import {IProductModel} from '../../cors/_models'
import {getProductsList} from '../../cors/_requests'
import Loader from '../../../common/Loader'
import Paginator from '../../../common/Paginator'
import NormalMessage from '../../../common/NormalMessage'
import SpecialSelectionTable from './SpecialSelectionTable'
import SpecialSelectionSubmit from './SpecialSelectionSubmit'
import {prepareFilters} from '../../../orders/core/_requests'
import {useCheckPendingBulk} from '../core/useCheckPendingBulk'
import {useIntl} from 'react-intl'
import './add-on-selected.css'

const SpecialSelection = () => {
  const {formatMessage} = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const {currentUser, language} = SharedData()
  const [requestList, setRequestList] = useState<IProductModel[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const [canFilter, setCanFilter] = useState(true)
  const [totalResult, setTotalResult] = useState<number>(0)
  const [errorMsg, setErrorMsg] = useState('')

  // Selection lives here so the sticky submit bar can sit outside the table
  // card (a card with overflow:hidden can't host a sticky child).
  const [selectedProductIDs, setSelectedProductIDs] = useState<number[]>([])
  const handleRowSelection = (productID: number) => {
    setSelectedProductIDs((prev) =>
      prev.includes(productID) ? prev.filter((id) => id !== productID) : [...prev, productID]
    )
  }

  // if user already has an uploaded bulk but not complete the process
  useCheckPendingBulk()

  useEffect(() => {
    getDate()
  }, [searchParams])

  const getDate = async () => {
    setIsLoading(true)
    try {
      prepareFilters(searchParams, setSearchParams)
      let queryParams = document.location.search
      if (queryParams === '') {
        queryParams += '?filter_status=1'
      } else {
        queryParams += '&filter_status=1'
      }
      const data = await getProductsList(queryParams, currentUser?.token, language)
      if (data) {
        setRequestList(data.data.data.products)
        setTotalResult(data.data.data.total_products)
      } else {
        setTotalResult(0)
        setRequestList([])
        setErrorMsg(formatMessage({id: 'no_data'}))
      }
    } catch (exp) {
      setErrorMsg(formatMessage({id: 'something_wrong_desc'}))
    }
    setIsLoading(false)
  }

  const resultsData = (): ReactNode => {
    return (
      <>
        <section className='aos-card aos-table-card'>
          <div className='aos-table-wrap'>
            <SpecialSelectionTable
              data={requestList}
              selectedProductIDs={selectedProductIDs}
              onToggle={handleRowSelection}
            />
          </div>
          <div className='aos-pagination'>
            <Paginator totalResult={totalResult} />
          </div>
        </section>

        {isLoading && <Loader isFull={true} />}
        {!isLoading && requestList.length === 0 && errorMsg !== '' && <NormalMessage msg={errorMsg} />}
      </>
    )
  }

  return (
    <div className='aos-root'>
      {/* Title & info */}
      <h1 className='aos-title'>{formatMessage({id: 'discounts'})}</h1>
      {/* Multi-supplier (B7): discounts attach to the supplier's own offer. */}
      <div className='aos-note' role='note'>
        <i className='fas fa-circle-info' aria-hidden='true' />
        <span>{formatMessage({id: 'discount_selection_offer_note'})}</span>
      </div>

      {/* Filters */}
      <section className='aos-card aos-card--pad aos-filters'>
        <div className='aos-card-head'>
          <h2 className='aos-filters-title'>{formatMessage({id: 'filters'})}</h2>
          <button type='button' className='aos-filter-chip' onClick={() => setCanFilter(!canFilter)}>
            {formatMessage({id: 'filter'})} <i className='fas fa-filter' aria-hidden='true' />
          </button>
        </div>
        {canFilter && <SpecialSelectionFilters />}
      </section>

      {resultsData()}

      {/* Sticky submit bar */}
      <SpecialSelectionSubmit products_id={selectedProductIDs} />
    </div>
  )
}

export default SpecialSelection
